import { useState } from 'react';
import {
  Calendar as CalendarIcon,
  Play,
  ChevronDown,
  ChevronLeft,
  Loader2,
  Send,
  MessageCircle,
  Sparkles,
  Check,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Carousel, Card } from '@/components/ui/apple-cards-carousel';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import NetworkContentEditor from '@/components/NetworkContentEditor';
import { CrossPostMenu } from '../CrossPostMenu';
import { PublicationTimeItems } from './PublicationTimeItems';
import { CrosspostToggle } from './CrosspostToggle';
import { InlineEditableV10 } from './InlineEditableV10';
import type { ContentEvent, PublicationItem, SocialConnection } from './types';
import type { PublishChannel } from '@/services/data/publishContent';

interface MobileLayoutProps {
  readonly event: ContentEvent;
  readonly isInline: boolean;
  readonly editingField: 'title' | 'description' | null;
  readonly setEditingField: (f: 'title' | 'description' | null) => void;
  readonly editTitle: string;
  readonly editDescription: string;
  readonly isValidated: boolean;
  readonly isValidating: boolean;
  readonly isPublishing: boolean;
  readonly showPublishMenu: boolean;
  readonly setShowPublishMenu: (v: boolean) => void;
  readonly globalCrosspostEnabled: boolean;
  readonly crosspostDisabled: boolean;
  readonly allItems: PublicationItem[];
  readonly isReadOnly: boolean;
  readonly hookVariations: Array<{
    id: string;
    hookTitle: string;
    hookText: string;
    coverUrl?: string;
    videoUrl?: string;
  }>;
  readonly isAbTestingEvent: boolean;
  readonly versionsEnabled: boolean[];
  readonly effectivePlatforms: SocialConnection[];
  readonly videoCompatiblePlatforms: SocialConnection[];
  readonly onClose: () => void;
  readonly onSaveTitle: (val: string) => void;
  readonly onSaveDescription: (val: string) => void;
  readonly onValidate: () => void;
  readonly onCrossPost: (channels: PublishChannel[]) => void;
  readonly onCrosspostToggle: (enabled: boolean) => void;
  readonly onToggleVersionEnabled: (i: number) => void;
  readonly onOpenVideo: (url: string) => void;
  readonly onOpenSchedule: () => void;
}

export function MobileLayout({
  event,
  isInline,
  editingField,
  setEditingField,
  editTitle,
  editDescription,
  isValidated,
  isValidating,
  isPublishing,
  showPublishMenu,
  setShowPublishMenu,
  globalCrosspostEnabled,
  crosspostDisabled,
  allItems,
  isReadOnly,
  hookVariations,
  isAbTestingEvent,
  versionsEnabled,
  effectivePlatforms,
  videoCompatiblePlatforms,
  onClose,
  onSaveTitle,
  onSaveDescription,
  onValidate,
  onCrossPost,
  onCrosspostToggle,
  onToggleVersionEnabled,
  onOpenVideo,
  onOpenSchedule,
}: MobileLayoutProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const actionBar = (
    <div className="space-y-4">
      {/* Publication times */}
      {event.publicationTimes && event.publicationTimes.length > 0 && (
        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-wider text-white/40">
            Heures de publication
          </p>
          <div className="grid grid-cols-3 gap-2">
            <PublicationTimeItems
              allItems={allItems}
              isValidated={isValidated}
              onOpenSchedule={onOpenSchedule}
            />
          </div>
        </div>
      )}

      {/* Cross-post toggle */}
      {globalCrosspostEnabled && (
        <CrosspostToggle
          enabled={!crosspostDisabled}
          onChange={onCrosspostToggle}
        />
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        {event.videoUrl && isInline && (
          <button
            onClick={() => onOpenVideo(event.videoUrl!)}
            className="rounded-xl py-3 px-4 text-sm font-medium transition-all flex items-center justify-center gap-2 bg-white/[0.03] text-white/80 border border-white/[0.06] hover:bg-white/[0.06]"
          >
            <MessageCircle className="h-4 w-4" />
          </button>
        )}

        <button
          onClick={onValidate}
          disabled={isValidating}
          className={`flex-1 rounded-xl py-3 px-4 text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
            isValidated
              ? 'bg-white/[0.03] text-white/80 border border-white/[0.06] hover:bg-white/[0.06]'
              : 'bg-white text-black hover:bg-white/90'
          }`}
        >
          {isValidating && <Loader2 className="h-4 w-4 animate-spin" />}
          {isValidated ? 'Invalider cette vidéo' : 'Valider la vidéo'}
        </button>

        {videoCompatiblePlatforms.length > 0 && isValidated && (
          <div className="relative">
            <button
              onClick={() => setShowPublishMenu(!showPublishMenu)}
              disabled={isPublishing}
              className="rounded-xl py-3 px-4 text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 bg-amber-500 text-black hover:bg-amber-400"
            >
              {isPublishing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
            {showPublishMenu && (
              <CrossPostMenu
                platforms={effectivePlatforms}
                isPublishing={isPublishing}
                onPublish={onCrossPost}
                onClose={() => setShowPublishMenu(false)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full w-full">
      {/* Back button for inline mode */}
      {isInline && (
        <div className="px-6 py-5 border-b border-white/[0.04] shrink-0">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-white/60 hover:text-white/80 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
            <span className="text-sm font-medium">Retour</span>
          </button>
        </div>
      )}

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 pt-6">
          {/* Header */}
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber-400/90">
                    <Play className="h-3 w-3" />
                    <span>Contenu</span>
                  </div>
                </div>
                {/* Date */}
                <div className="flex items-center gap-2 text-xs text-white/60 mb-2">
                  <CalendarIcon className="h-3 w-3" />
                  <span>
                    {event.date.toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                </div>

                {/* Title — uses InlineEditableV10 for unified editing */}
                {editingField === 'title' && !isReadOnly ? (
                  <InlineEditableV10
                    value={editTitle}
                    onSave={onSaveTitle}
                    onCancel={() => setEditingField(null)}
                    fieldType="title"
                  />
                ) : (
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => { if (!isReadOnly) setEditingField('title'); }}
                  >
                    <h2 className="text-2xl text-white tracking-tight leading-tight">
                      {editTitle || event.title}
                    </h2>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Description — hidden for in_production */}
          {event.contentStatus !== 'in_production' && (
            <div className="mt-4 space-y-2 pb-2 sm:mt-6 sm:pb-6">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-white/40">
                  Description
                </p>
              </div>
              <div className="space-y-3">
                {editingField === 'description' && !isReadOnly ? (
                  <InlineEditableV10
                    value={editDescription}
                    onSave={onSaveDescription}
                    onCancel={() => setEditingField(null)}
                    fieldType="description"
                  />
                ) : (
                  <>
                    <p
                      role="none"
                      className={`text-sm text-white/70 leading-relaxed whitespace-pre-line cursor-pointer ${
                        !isExpanded ? 'line-clamp-3' : ''
                      }`}
                      onClick={() => {
                        setIsExpanded(true);
                        setEditingField('description');
                      }}
                    >
                      {event.description ?? ''}
                    </p>
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors outline-none focus:outline-none"
                    >
                      <span>{isExpanded ? 'Voir moins' : 'Voir plus'}</span>
                      <ChevronDown
                        className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    </button>
                  </>
                )}

                {/* Network-specific content adaptation */}
                {event.channels && event.channels.length > 0 && (
                  <Collapsible className="mt-3 pb-2 sm:mt-6 sm:pb-6">
                    <CollapsibleTrigger className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-white/40 hover:text-white/60 transition-colors w-full">
                      <Sparkles className="h-3 w-3" />
                      <span>Contenu adapté par réseau</span>
                      <ChevronDown className="h-3 w-3 ml-auto transition-transform ui-open:rotate-180" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-4">
                      <div className="glass rounded-xl p-4 border border-white/10">
                        <NetworkContentEditor
                          videoContentId={event.id}
                          originalTitle={event.title}
                          originalDescription={event.description ?? ''}
                          enabledNetworks={event.channels}
                        />
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Video carousel — hidden for in_production */}
        {(event.coverUrl || event.videoUrl) && event.contentStatus !== 'in_production' && (
          <div className="mt-3 pb-4 sm:mt-6 sm:pb-6 w-full">
            <Carousel
              items={hookVariations.map((hook, index) => (
                <div key={hook.id} className="flex flex-col items-center gap-2">
                  {/* Version label + checkbox (mobile only, multihook) */}
                  {!isInline && isAbTestingEvent && hookVariations.length > 1 && (
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'text-[10px] font-semibold uppercase tracking-[0.05em] px-2 py-0.5 rounded-full',
                          !versionsEnabled[index]
                            ? 'bg-[#1f1f1f] text-white/25'
                            : 'bg-[#1f1f1f] text-white/60',
                        )}
                      >
                        Version {String.fromCodePoint(65 + index)}
                      </span>
                      {!isReadOnly && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleVersionEnabled(index);
                          }}
                          className={cn(
                            'w-4 h-4 rounded border flex items-center justify-center transition-all',
                            versionsEnabled[index]
                              ? 'border-transparent'
                              : 'border-white/20 bg-transparent',
                          )}
                          style={
                            versionsEnabled[index]
                              ? {
                                  background: 'hsl(var(--v10-status-success))',
                                  borderColor: 'hsl(var(--v10-status-success))',
                                }
                              : {}
                          }
                        >
                          <AnimatePresence>
                            {versionsEnabled[index] && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0 }}
                                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                              >
                                <Check className="h-2.5 w-2.5 text-white" />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </button>
                      )}
                    </div>
                  )}
                  <div
                    className={cn(
                      'transition-all',
                      !isInline &&
                        isAbTestingEvent &&
                        !versionsEnabled[index] &&
                        'opacity-30 grayscale',
                    )}
                  >
                    <Card
                      card={{
                        src: hook.coverUrl,
                        title: hook.hookTitle,
                        category: hook.hookText,
                        videoUrl: versionsEnabled[index] ? hook.videoUrl : undefined,
                      }}
                      index={index}
                      layout={true}
                    />
                  </div>
                </div>
              ))}
              initialScroll={0}
              onOpenVideo={onOpenVideo}
            />
          </div>
        )}

        {/* Inline action bar (scrollable) */}
        {isInline && (
          <div className="border-t border-white/10 px-6 py-4 mt-6">
            {actionBar}
          </div>
        )}
      </div>

      {/* Fixed bottom bar for modal mode */}
      {!isInline && (
        <div className="relative flex-shrink-0 border-t border-white/10 px-6 py-4 bg-black/80 backdrop-blur-sm">
          {actionBar}
        </div>
      )}
    </div>
  );
}
